import React, { useState, useEffect } from 'react';
import { apiService, SuperAdminOffering, SuperAdminOfferingUpdate, LinkPerksBusinessResponse, RedemptionMode } from '../services/api';
import { uploadImageToS3, isS3Configured } from '../services/s3Upload';
import { DragDropImageUpload } from './DragDropImageUpload';
import './SuperAdminViews.css';

// Redemption mode options (drives the FE redemption flow)
const REDEMPTION_MODES: { value: RedemptionMode; label: string }[] = [
  { value: 'in_store', label: 'In-store' },
  { value: 'webpage', label: 'Website' },
  { value: 'url_with_code', label: 'Website + code' },
];

// Distance scope options - Global uses 100,000 km (100 million meters) to truly cover the entire Earth
const DISTANCE_SCOPES = {
  local: { label: 'Local', value: 10000, description: '~10 km radius' },
  provincial: { label: 'Provincial', value: 100000, description: '~100 km radius' },
  national: { label: 'National', value: 2500000, description: '~2,500 km radius' },
  global: { label: 'Global', value: 100000000, description: 'Worldwide (no limit)' },
} as const;

type DistanceScope = keyof typeof DISTANCE_SCOPES;

interface BusinessLocation {
  id: number;
  address: string;
}

// Helper to determine scope from distance value
const getDistanceScopeFromValue = (distance?: number): DistanceScope => {
  if (!distance || distance >= DISTANCE_SCOPES.global.value) return 'global';
  if (distance >= DISTANCE_SCOPES.national.value) return 'national';
  if (distance >= DISTANCE_SCOPES.provincial.value) return 'provincial';
  return 'local';
};

// Helper function to render text with clickable links
const renderTextWithLinks = (text: string, maxLength?: number): React.ReactNode => {
  // Truncate if needed
  const displayText = maxLength && text.length > maxLength 
    ? text.substring(0, maxLength) + '...' 
    : text;
  
  // URL regex pattern - use separate regex for split and test
  const urlPattern = /(https?:\/\/[^\s]+)/g;
  const parts = displayText.split(urlPattern);
  
  return parts.map((part, index) => {
    // Check if this part looks like a URL
    if (part.match(/^https?:\/\//)) {
      return (
        <a 
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{ 
            color: '#4299e1', 
            textDecoration: 'underline',
            wordBreak: 'break-all'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

const AllOfferingsView: React.FC = () => {
  const [offerings, setOfferings] = useState<SuperAdminOffering[]>([]);
  const [businesses, setBusinesses] = useState<LinkPerksBusinessResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  
  // Edit modal state
  const [editingOffering, setEditingOffering] = useState<SuperAdminOffering | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editImage, setEditImage] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const [editDistanceScope, setEditDistanceScope] = useState<DistanceScope>('local');
  const [editRedemptionMode, setEditRedemptionMode] = useState<RedemptionMode>('in_store');
  const [editWebsiteUrl, setEditWebsiteUrl] = useState('');
  const [editCode, setEditCode] = useState('');
  // Single-use is the exception; deals are unlimited-use by default.
  const [editIsSingleUse, setEditIsSingleUse] = useState(false);
  const [editSelectedLocations, setEditSelectedLocations] = useState<number[]>([]);
  const [businessLocations, setBusinessLocations] = useState<BusinessLocation[]>([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [offeringsData, businessesData] = await Promise.all([
        apiService.getSuperAdminOfferings(),
        apiService.getAllBusinessesNearby(),
      ]);
      setOfferings(offeringsData);
      setBusinesses(Array.isArray(businessesData) ? businessesData : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchOfferings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getSuperAdminOfferings();
      setOfferings(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch offerings');
    } finally {
      setLoading(false);
    }
  };

  // Get locations for a specific business
  const getBusinessLocations = (businessId?: number): BusinessLocation[] => {
    if (!businessId) return [];
    const business = businesses.find(b => b.id === businessId);
    if (!business?.locations) return [];
    
    return business.locations
      .filter((loc: any) => loc.id !== undefined)
      .map((loc: any) => ({
        id: loc.id as number,
        address: loc.address || `Location ${loc.id}`,
      }));
  };

  const filteredOfferings = offerings.filter(offering => {
    const matchesSearch = 
      offering.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (offering.description?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (offering.business_name?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const isActive = offering.is_active !== false;
    const matchesFilter = 
      filterStatus === 'all' || 
      (filterStatus === 'active' && isActive) ||
      (filterStatus === 'inactive' && !isActive);
    
    return matchesSearch && matchesFilter;
  });

  const toggleOfferingStatus = async (offering: SuperAdminOffering) => {
    const newIsActive = !(offering.is_active !== false);
    
    try {
      const updateData: SuperAdminOfferingUpdate = {
        name: offering.name,
        description: offering.description,
        type: offering.type,
        image: offering.image,
        code: offering.code,
        expiration_date: offering.expiration_date,
        amount: offering.amount,
        distance: offering.distance,
        is_active: newIsActive,
        redemption_mode: offering.redemption_mode,
        website_url: offering.website_url,
        is_single_use: offering.is_single_use,
        location_ids: offering.location_ids,
      };

      await apiService.updateSuperAdminOffering(offering.id, updateData);
      
      // Update local state
      setOfferings(prev => 
        prev.map(o => o.id === offering.id ? { ...o, is_active: newIsActive } : o)
      );
    } catch (err) {
      alert('Failed to update offering status: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const openEditModal = (offering: SuperAdminOffering) => {
    setEditingOffering(offering);
    setEditName(offering.name);
    setEditDescription(offering.description || '');
    setEditImage(offering.image || '');
    setPreviewImage(offering.image || '');
    setEditDistanceScope(getDistanceScopeFromValue(offering.distance));

    // Redemption settings - prefer redemption_mode, fall back to legacy is_ecom
    setEditRedemptionMode(
      offering.redemption_mode || (offering.is_ecom ? 'url_with_code' : 'in_store'),
    );
    setEditWebsiteUrl(offering.website_url || '');
    setEditCode(offering.code || '');
    setEditIsSingleUse(offering.is_single_use === true);
    
    // Load business locations and set selected ones
    const locations = getBusinessLocations(offering.business_id);
    setBusinessLocations(locations);
    
    // Set selected locations - if location_ids is empty/undefined, select all locations by default
    if (offering.location_ids && offering.location_ids.length > 0) {
      setEditSelectedLocations(offering.location_ids);
    } else {
      // Default to all locations if none specified
      setEditSelectedLocations(locations.map(l => l.id));
    }
    
    setSaveError(null);
    setSaveSuccess(false);
  };

  const closeEditModal = () => {
    setEditingOffering(null);
    setEditName('');
    setEditDescription('');
    setEditImage('');
    setPreviewImage('');
    setEditDistanceScope('local');
    setEditRedemptionMode('in_store');
    setEditWebsiteUrl('');
    setEditCode('');
    setEditIsSingleUse(false);
    setEditSelectedLocations([]);
    setBusinessLocations([]);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleImageUpload = async (file: File) => {
    // Show local preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setPreviewImage(result);
    };
    reader.readAsDataURL(file);

    // Upload to S3
    setUploadingImage(true);
    setSaveError(null);

    try {
      if (!isS3Configured()) {
        throw new Error('S3 is not configured. Please check your environment variables.');
      }

      const s3Url = await uploadImageToS3(file);
      setEditImage(s3Url);
      setPreviewImage(s3Url);
    } catch (err: any) {
      console.error('Image upload failed:', err);
      setSaveError(err.message || 'Failed to upload image. Please try again.');
      setPreviewImage(editImage); // Reset to previous image
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingOffering) return;

    const requiresWebsiteUrl =
      editRedemptionMode === 'webpage' || editRedemptionMode === 'url_with_code';
    const requiresCode = editRedemptionMode === 'url_with_code';

    // Client-side validation matching backend rules
    if (requiresWebsiteUrl && !editWebsiteUrl.trim()) {
      setSaveError('A website URL is required for website-based redemption modes.');
      return;
    }
    if (requiresCode && !editCode.trim()) {
      setSaveError('A discount code is required for the "Website + code" redemption mode.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    // Get the distance value from the selected scope
    const newDistance = DISTANCE_SCOPES[editDistanceScope].value;
    const finalWebsiteUrl = requiresWebsiteUrl ? editWebsiteUrl.trim() : undefined;
    const finalCode = requiresCode ? editCode : editingOffering.code;

    try {
      const updateData: SuperAdminOfferingUpdate = {
        name: editName,
        description: editDescription,
        type: editingOffering.type,
        image: editImage || undefined,
        code: finalCode,
        expiration_date: editingOffering.expiration_date,
        amount: editingOffering.amount,
        distance: newDistance,
        is_active: editingOffering.is_active,
        redemption_mode: editRedemptionMode,
        website_url: finalWebsiteUrl,
        is_single_use: editIsSingleUse,
        location_ids: editSelectedLocations.length > 0 ? editSelectedLocations : undefined,
      };

      await apiService.updateSuperAdminOffering(editingOffering.id, updateData);
      
      // Update local state with all changed fields
      setOfferings(prev => 
        prev.map(o => o.id === editingOffering.id ? { 
          ...o, 
          name: editName, 
          description: editDescription, 
          image: editImage,
          distance: newDistance,
          redemption_mode: editRedemptionMode,
          website_url: finalWebsiteUrl,
          code: finalCode,
          is_single_use: editIsSingleUse,
          location_ids: editSelectedLocations.length > 0 ? editSelectedLocations : undefined,
        } : o)
      );
      
      setSaveSuccess(true);
      setTimeout(() => {
        closeEditModal();
      }, 1500);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Toggle location selection
  const toggleLocationSelection = (locationId: number) => {
    setEditSelectedLocations(prev => {
      if (prev.includes(locationId)) {
        return prev.filter(id => id !== locationId);
      } else {
        return [...prev, locationId];
      }
    });
  };

  // Select/deselect all locations
  const toggleAllLocations = () => {
    if (editSelectedLocations.length === businessLocations.length) {
      setEditSelectedLocations([]);
    } else {
      setEditSelectedLocations(businessLocations.map(l => l.id));
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getOfferingTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      'percentage_discount': 'Percentage Off',
      'fixed_discount': 'Fixed Discount',
      'bogo': 'BOGO',
      'free_item': 'Free Item',
      'service': 'Service',
    };
    return typeMap[type] || type;
  };

  if (loading) {
    return (
      <div className="admin-view">
        <div className="loading-state">
          <p>Loading offerings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-view">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchOfferings} className="add-button">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-view">
      <div className="view-header">
        <div>
          <h2>All Offerings</h2>
          <p className="view-description">View and manage all offerings across the platform</p>
        </div>
        <button onClick={fetchData} className="add-button">
          🔄 Refresh
        </button>
      </div>

      <div className="view-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, description, or business..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-box">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'inactive')}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-value">{offerings.length}</div>
          <div className="stat-label">Total Offerings</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{offerings.filter(o => o.is_active !== false).length}</div>
          <div className="stat-label">Active</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{offerings.filter(o => o.is_active === false).length}</div>
          <div className="stat-label">Inactive</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{new Set(offerings.map(o => o.business_id)).size}</div>
          <div className="stat-label">Businesses</div>
        </div>
      </div>

      {filteredOfferings.length === 0 ? (
        <div className="empty-state">
          <p>No offerings found matching your criteria.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Offering Name</th>
                <th>Business</th>
                <th>Type</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOfferings.map(offering => {
                const isActive = offering.is_active !== false;
                return (
                  <tr key={offering.id}>
                    <td>
                      <div className="name-cell">
                        {offering.image ? (
                          <img 
                            src={offering.image} 
                            alt={offering.name} 
                            style={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: '8px', 
                              objectFit: 'cover',
                              opacity: isActive ? 1 : 0.5
                            }}
                          />
                        ) : (
                          <div className="avatar" style={{ backgroundColor: isActive ? '#0d1b2a' : '#a0aec0' }}>
                            🎁
                          </div>
                        )}
                        <div>
                          <strong>{offering.name}</strong>
                          <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                            {offering.description ? renderTextWithLinks(offering.description, 50) : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>{offering.business_name || `Business #${offering.business_id}`}</td>
                    <td>
                      <span className="type-badge">{getOfferingTypeLabel(offering.type)}</span>
                    </td>
                    <td>{formatDate(offering.expiration_date)}</td>
                    <td>
                      <span className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons-group">
                        <button
                          className="action-button edit"
                          onClick={() => openEditModal(offering)}
                        >
                          ✏️ Edit
                        </button>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={isActive}
                            onChange={() => toggleOfferingStatus(offering)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {editingOffering && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Offering</h3>
              <button className="modal-close" onClick={closeEditModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="editName">Offering Name</label>
                <input
                  type="text"
                  id="editName"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Enter offering name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="editDescription">Description</label>
                <textarea
                  id="editDescription"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Enter description"
                  rows={4}
                />
              </div>
              <div className="form-group">
                <DragDropImageUpload
                  label="Offering Image"
                  onFileSelect={handleImageUpload}
                  previewUrl={previewImage}
                  uploading={uploadingImage}
                  placeholder="Drop image here or click to browse"
                />
              </div>

              {/* Redemption Method */}
              <div className="form-group">
                <label htmlFor="editRedemptionMode">Redemption Method</label>
                <select
                  id="editRedemptionMode"
                  value={editRedemptionMode}
                  onChange={(e) => {
                    const mode = e.target.value as RedemptionMode;
                    setEditRedemptionMode(mode);
                    if (mode === 'in_store') {
                      setEditWebsiteUrl('');
                      setEditCode('');
                    } else if (mode === 'webpage') {
                      setEditCode('');
                    }
                  }}
                  disabled={saving}
                >
                  {REDEMPTION_MODES.map(mode => (
                    <option key={mode.value} value={mode.value}>{mode.label}</option>
                  ))}
                </select>
              </div>

              {(editRedemptionMode === 'webpage' || editRedemptionMode === 'url_with_code') && (
                <div className="form-group">
                  <label htmlFor="editWebsiteUrl">Website URL</label>
                  <p className="field-hint">
                    {editRedemptionMode === 'url_with_code'
                      ? 'Use {code} as a placeholder, or the code will be appended'
                      : 'Where customers are sent to redeem'}
                  </p>
                  <input
                    type="url"
                    id="editWebsiteUrl"
                    value={editWebsiteUrl}
                    onChange={(e) => setEditWebsiteUrl(e.target.value)}
                    placeholder="https://shop.example.com/deal"
                    disabled={saving}
                  />
                </div>
              )}

              {editRedemptionMode === 'url_with_code' && (
                <div className="form-group">
                  <label htmlFor="editCode">Discount Code</label>
                  <input
                    type="text"
                    id="editCode"
                    value={editCode}
                    onChange={(e) => setEditCode(e.target.value.toUpperCase())}
                    placeholder="e.g., SAVE10"
                    maxLength={200}
                    disabled={saving}
                  />
                </div>
              )}

              {/* Redemption limit: unlimited by default, single-use is the exception */}
              <div className="form-group">
                <label htmlFor="editIsSingleUse">Redemption Limit</label>
                <label className="location-checkbox-label">
                  <input
                    type="checkbox"
                    id="editIsSingleUse"
                    checked={editIsSingleUse}
                    onChange={(e) => setEditIsSingleUse(e.target.checked)}
                    disabled={saving}
                  />
                  <span>One-time use only (single redemption per user)</span>
                </label>
                <p className="field-hint">
                  Leave off so customers can redeem this deal an unlimited number of times. Turn on only for exclusive, single-use offers.
                </p>
              </div>

              {/* Location Selection */}
              {businessLocations.length > 0 && (
                <div className="form-group">
                  <label>Applicable Locations</label>
                  <p className="field-hint">Select which business locations this offer applies to</p>
                  <div className="location-selection-container">
                    <div className="location-select-all">
                      <label className="location-checkbox-label">
                        <input
                          type="checkbox"
                          checked={editSelectedLocations.length === businessLocations.length}
                          onChange={toggleAllLocations}
                          disabled={saving}
                        />
                        <span className="checkbox-text">
                          {editSelectedLocations.length === businessLocations.length ? 'Deselect All' : 'Select All'}
                        </span>
                      </label>
                      <span className="location-count">
                        {editSelectedLocations.length} of {businessLocations.length} selected
                      </span>
                    </div>
                    <div className="location-options">
                      {businessLocations.map((location) => (
                        <label 
                          key={location.id} 
                          className={`location-option ${editSelectedLocations.includes(location.id) ? 'selected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={editSelectedLocations.includes(location.id)}
                            onChange={() => toggleLocationSelection(location.id)}
                            disabled={saving}
                          />
                          <div className="location-content">
                            <span className="location-icon">📍</span>
                            <span className="location-address">{location.address}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {businessLocations.length === 0 && editingOffering?.business_id && (
                <div className="form-group">
                  <label>Applicable Locations</label>
                  <p className="field-hint-warning">
                    ⚠️ No locations found for this business. The offer will apply to all locations by default.
                  </p>
                </div>
              )}

              <div className="form-group">
                <label>Visibility Radius</label>
                <p className="field-hint">How far from the business location should this offer be visible to users?</p>
                <div className="distance-scope-options">
                  {(Object.keys(DISTANCE_SCOPES) as DistanceScope[]).map((scope) => (
                    <label 
                      key={scope} 
                      className={`scope-option ${editDistanceScope === scope ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="editDistanceScope"
                        value={scope}
                        checked={editDistanceScope === scope}
                        onChange={() => setEditDistanceScope(scope)}
                        disabled={saving}
                      />
                      <div className="scope-content">
                        <span className="scope-label">{DISTANCE_SCOPES[scope].label}</span>
                        <span className="scope-description">{DISTANCE_SCOPES[scope].description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {saveError && (
                <div className="error-message">{saveError}</div>
              )}

              {saveSuccess && (
                <div className="success-message">Offering updated successfully!</div>
              )}
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={closeEditModal} disabled={saving || uploadingImage}>
                Cancel
              </button>
              <button className="submit-button" onClick={handleSaveEdit} disabled={saving || uploadingImage}>
                {saving ? 'Saving...' : uploadingImage ? 'Wait for upload...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllOfferingsView;
